import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  $DCDangerColor,
  $DCDangerLightColor,
  $DCInfoColor,
  $DCInfoLightColor,
  $DCprimaryColor,
  $DCprimaryLightColor,
  $DCSuccessColor,
  $DCSuccessLightColor,
  $DCWarningColor,
  $DCWarningLightColor,
  DcBreadcrumbs,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcIcon,
  DcOverlaySlider,
  DcTag,
  DcText,
  PixelCurrency,
  PixelDate,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionActions,
  SectionContent,
} from 'pixel-base'
import queryString from 'query-string'
import { getAllComplaints } from 'utils/api-routes.util'

import SupportDetail from './SupportDetail'

const breadcrumbs = [
  {
    name: 'Customer Support',
    active: true,
  },
]

const columns = [
  {
    dataKey: 'id',
    name: 'Number',
    sortable: true,
    selector: (row) => (
      <DcTag textColor="#374151" customBackgroundColor="#e7e8ea">
        {row.complaint_id}
      </DcTag>
    ),
  },
  {
    dataKey: 'created_at',
    name: 'Date',
    sortable: true,

    selector: (row) => (
      <PixelDate
        className="textSmall"
        value={row?.created_at}
        format={'pixelStandard'}
      />
    ),
  },
  {
    dataKey: 'is_resolved',
    name: 'Status',

    sortable: true,
    selector: (row) =>
      row.is_resolved ? (
        <DcTag textColor="#f2bc16" customBackgroundColor="#fdf7e3">
          closed
        </DcTag>
      ) : (
        <DcTag textColor="#df3c4e" customBackgroundColor="#fbe8ea">
          open
        </DcTag>
      ),
  },
  {
    dataKey: 'complaint_type',
    name: 'Type',

    sortable: true,
    selector: (row) => (
      <DcTag
        textColor={
          row.complaint_type == 'service'
            ? '#f2bc16'
            : row.complaint_type == 'product'
            ? '#df3c4e'
            : row.complaint_type == 'courier'
            ? '#374151'
            : ''
        }
        customBackgroundColor={
          row.complaint_type == 'service'
            ? '#fdf7e3'
            : row.complaint_type == 'product'
            ? '#fbe8ea'
            : row.complaint_type == 'courier'
            ? '#e7e8ea'
            : ''
        }
      >
        {row.complaint_type}
      </DcTag>
    ),
  },
  {
    dataKey: 'order_id',
    name: 'Order Id',
    sortable: true,

    selector: (row) => row.order_id || '--',
  },
  {
    dataKey: 'customer_name',
    name: 'Customer',
    sortable: true,

    selector: (row) => row.customer_name || '--',
  },
  {
    dataKey: 'last_note',
    name: 'Last Note',
    width: '15%',
    sortable: true,
    selector: (row) => (
      <DcText
        multiLine={true}
        charLimit="25"
        toolTipText={row.last_note || '--'}
        showToolTip={true}
      >
        {row.last_note || '--'}
      </DcText>
    ),
  },
  {
    dataKey: 'order_total',
    name: 'Order Total ',
    sortable: true,

    style: {
      color: '#f44336',
    },
    selector: (row) =>
      <PixelCurrency value={row.order_total} hideSymbol={true} /> || '--',
  },
  {
    dataKey: 'resolution_type',
    name: 'Resolution Type ',
    sortable: true,

    style: {
      color: '#f44336',
    },
    selector: (row) => (
      <DcTag
        textColor={
          row.resolution_type === 'exchange'
            ? '#f2bc16' // Warning
            : row.resolution_type === 'refund'
            ? $DCSuccessColor // Success
            : row.resolution_type === 'other'
            ? '#374151' // Secondary
            : row.resolution_type === 'partial_refund'
            ? $DCDangerColor // Danger
            : row.resolution_type === 'partial_exchange'
            ? $DCWarningColor // Warning
            : row.resolution_type === 'partials'
            ? $DCInfoColor // Info
            : row.resolution_type === 'replacement'
            ? $DCprimaryColor // Primary
            : '#000' // Default (Fallback)
        }
        customBackgroundColor={
          row.resolution_type === 'exchange'
            ? '#fdf7e3' // Light Warning
            : row.resolution_type === 'refund'
            ? $DCSuccessLightColor // Light Success
            : row.resolution_type === 'other'
            ? '#e7e8ea' // Light Secondary
            : row.resolution_type === 'partial_refund'
            ? $DCDangerLightColor // Light Danger
            : row.resolution_type === 'partial_exchange'
            ? $DCWarningLightColor // Light Warning
            : row.resolution_type === 'partials'
            ? $DCInfoLightColor // Light Info
            : row.resolution_type === 'replacement'
            ? $DCprimaryLightColor // Light Primary
            : '#fff' // Default (Fallback)
        }
      >
        {row.resolution_type}
      </DcTag>
    ),
  },
  {
    dataKey: 'created_by',
    name: 'Created By',
    sortable: true,

    style: {
      color: '#2196f3',
    },
    selector: (row) => row.created_by || '--',
  },
]

const statusFilterArray = [
  { value: 'any', label: 'Any' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
]

const typeFilterArray = [
  { value: 'any', label: 'Any' },
  { value: 'product', label: 'Product' },
  { value: 'service', label: 'Service' },
  { value: 'courier', label: 'Courier' },
  { value: 'other', label: 'Other' },
]

const Support = () => {
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const [data, setData] = React.useState<any>([])
  const [showDetailModal, setShowDetailModal] = React.useState<any>(false)
  const [typeId, setTypeId] = React.useState<any>(null)
  const { complaintId } = React.useContext(OrdersContext)

  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState<any>({
    store_id: p.store_id || null,
    page: p.page || 0,
    status: p.status || 'any',
    type: p.type || 'any',
    id: p.id || null,
  })
  const [tableLoading, setTableLoading] = React.useState(false)

  const [totalRows, setTotalRows] = React.useState(0)

  React.useEffect(() => {
    if (filters.store_id !== null) {
      navigate({
        pathname: '',
        search: queryString.stringify(filters),
      })
    }
  }, [filters])
  React.useEffect(() => {
    navigate({
      pathname: '/support',
      search: queryString.stringify(filters),
    })
    getOrders()
  }, [filters])
  React.useEffect(() => {
    if (complaintId) {
      setTypeId(complaintId)
      setShowDetailModal(true)
    }
  }, [complaintId])
  React.useEffect(() => {
    const params: any = searchParams
    if (params.store_id) setFilters(params)
  }, [filters])

  const handlePageChange = (page) => {
    if (filters.page === page - 1) return
    setFilters({
      ...filters,
      page: page - 1,
    })
  }
  const getOrders = () => {
    setTableLoading(true)
    getAllComplaints(filters).subscribe((res: any) => {
      setData(res.response?.data || [])
      setTotalRows(res.response._meta?.total || 0)
      setTableLoading(false)
    })
  }
  React.useEffect(() => {
    if (filters.id) {
      setTypeId(filters.id)
      setShowDetailModal(true)
    }
  }, [filters.id])

  const onRowClicked = (rowData) => {
    setTypeId(rowData.complaint_id)
    setShowDetailModal(true)
  }
  return (
    <DcFlexBox flexDirection="column">
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: '1px solid #e5e5e5',
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <SectionActions />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>

      <PixelPageContainer
        style={{ padding: '0px', maxHeight: 'calc(100vh - 60px)' }}
      >
        <Section style={{ padding: '0px', height: 'auto' }}>
          <SectionContent style={{ padding: '0px' }}>
            <DcFlexBox
              height="auto"
              alignItems="flex-end"
              padding="0px 10px"
              justifyContent="flex-end"
            >
              <DcFlexBox width="40%" padding="5px 10px" gap="10px">
                <DcDropDown
                  dropdownSize="sm"
                  placeholder="Status"
                  selectlabel={'Status'}
                  padding="0px"
                  style={{ minheight: '30px !important' }}
                  required
                  options={statusFilterArray}
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value })
                  }}
                />

                <DcDropDown
                  dropdownSize="sm"
                  placeholder="Type"
                  selectlabel={'Type'}
                  required
                  options={typeFilterArray}
                  value={filters.type}
                  onChange={(e) => {
                    setFilters({ ...filters, type: e.target.value })
                  }}
                />
              </DcFlexBox>
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section
          style={{
            padding: '20px',
            height: 'calc(100vh - 102px)',
            overflow: 'auto',
          }}
        >
          <SectionContent style={{ padding: '10px' }}>
            <PixelTable
              pagination={true}
              columns={columns}
              progressPending={tableLoading}
              data={data}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 220px)"
              paginationServer
              paginationTotalRows={totalRows}
              highlightOnHover={true}
              pointerOnHover={true}
              paginationPerPage={50}
              paginationDefaultPage={Number(filters.page) + 1}
              onChangePage={handlePageChange}
              onRowClicked={onRowClicked}
              customStyles={{
                subHeader: {
                  style: {
                    padding: '0px !important',
                  },
                },
              }}
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <DcOverlaySlider
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Detail"
        padding="0px"
      >
        <SupportDetail typeId={typeId} />
      </DcOverlaySlider>
    </DcFlexBox>
  )
}

export default Support
